import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AIRAMark } from '@/components/brand/Marks';
import { Play, CheckCircle, Clock, AlertTriangle, Loader2, Users, Car, Home, Shield, RefreshCw } from 'lucide-react';

const AGENT_ICONS = { genesis: Users, motaira: Car, propaira: Home, claimai: AlertTriangle, validai: Shield };
const STATUS_COLORS = { pending: '#5C6B7A', running: '#00B4D8', complete: '#1D9E75', error: '#E24B4A' };

export default function MARSPage() {
  const api = useApi();
  const { user } = useAuth();
  const [agents, setAgents] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [polling, setPolling] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadStatus = useCallback(async () => {
    try {
      const [agentsRes, statusRes] = await Promise.all([api.get('/mars/agents'), api.get('/mars/status')]);
      setAgents(agentsRes.data);
      setStatus(statusRes.data);
      return statusRes.data?.status === 'running';
    } catch (err) { console.error("API error:", err?.message); }
  }, [api]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadStatus().then(r => { if (r) setPolling(true); }).finally(() => setLoading(false));
  }, [loadStatus]);

  // Poll while running
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!polling) return;
    const timer = setInterval(async () => {
      const stillRunning = await loadStatus();
      if (!stillRunning) { setPolling(false); clearInterval(timer); }
    }, 3000);
    return () => clearInterval(timer);
  }, [polling, loadStatus]);

  const handleStart = async () => {
    setStarting(true);
    try {
      await api.post('/mars/run', {});
      setPolling(true);
      setTimeout(() => loadStatus(), 1000);
    } catch (err) { console.error("API error:", err?.message); }
    setStarting(false);
  };

  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const isRunning = status?.status === 'running';
  const isComplete = status?.status === 'complete';

  if (loading) return <div data-testid="mars-loading"><Skeleton className="h-64 rounded-sm" /></div>;

  return (
    <div className="space-y-6" data-testid="mars-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans'" }}>MARS</h1>
            <span className="text-xs px-2 py-0.5 rounded-sm font-medium" style={{ background: 'rgba(0,180,216,0.1)', color: '#00B4D8' }}>Multi-Agent Reasoning System</span>
          </div>
          <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>5 AI agents working in pipeline to generate and validate policyholder data</p>
        </div>
        {isRunning && (
          <Button size="sm" onClick={() => loadStatus()} className="rounded-sm text-xs" variant="outline">
            <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Refreshing...
          </Button>
        )}
      </div>

      {/* Start Panel */}
      {isAdmin && !isRunning && (
        <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="mars-start">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="text-sm font-medium" style={{ color: '#0D1B2A' }}>Regenerate the synthetic policyholder book</div>
              <p className="text-[11px] mt-1" style={{ color: '#5C6B7A' }}>Generates ~128,000 policyholders across 13 classes (fixed per-segment distribution). Replaces the existing book — takes a few minutes with live progress below.</p>
            </div>
            <Button data-testid="mars-run-btn" onClick={handleStart} disabled={starting}
              className="rounded-sm font-semibold px-6" style={{ background: '#00B4D8' }}>
              {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              Run MARS Pipeline
            </Button>
          </div>
        </Card>
      )}

      {/* Agent Pipeline */}
      <div className="space-y-2" data-testid="mars-agents">
        {Object.entries(agents).map(([id, agent], idx) => {
          const agentStatus = status?.agents?.[id] || {};
          const st = agentStatus.status || 'pending';
          const Icon = AGENT_ICONS[id] || Shield;
          const color = STATUS_COLORS[st];
          const processed = agentStatus.processed || 0;
          const pct = status?.target ? Math.min(100, (processed / status.target) * 100) : 0;

          return (
            <Card key={id} data-testid={`mars-agent-${id}`}
              className="rounded-sm border bg-white overflow-hidden transition-all"
              style={{ borderColor: st === 'running' ? '#00B4D8' : 'rgba(136,150,168,0.2)' }}>
              <div className="flex items-center">
                {/* Pipeline connector */}
                <div className="w-1.5 h-full self-stretch" style={{ background: color }} />
                <div className="flex-1 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: color + '15' }}>
                        {st === 'running' ? <Loader2 className="w-4 h-4 animate-spin" style={{ color }} /> : st === 'complete' ? <CheckCircle className="w-4 h-4" style={{ color }} /> : <Icon className="w-4 h-4" style={{ color }} />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold" style={{ color: '#0D1B2A' }}>{agent.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm capitalize font-medium" style={{ background: color + '15', color }}>{st}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(136,150,168,0.08)', color: '#5C6B7A' }}>{agent.type}</span>
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: '#5C6B7A' }}>{agent.role} — {agent.desc.split('.')[0]}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold font-mono" style={{ color: '#0D1B2A' }}>{processed.toLocaleString()}</span>
                      <p className="text-[10px]" style={{ color: '#5C6B7A' }}>processed</p>
                    </div>
                  </div>
                  {(st === 'running' || st === 'complete') && (
                    <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.1)' }}>
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Stats */}
      {(isComplete || isRunning) && status?.stats && (
        <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="mars-stats">
          <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Generation Results</h3>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
            {[
              { label: 'Total', value: status.stats.total?.toLocaleString(), color: '#0D1B2A' },
              { label: 'Policies', value: status.stats.total_policies?.toLocaleString(), color: '#1A4C7C' },
              { label: 'Avg / Client', value: status.stats.avg_policies_per_client, color: '#5C6B7A' },
              { label: 'SME Commercial', value: status.stats.sme_commercial?.toLocaleString(), color: '#00B4D8' },
              { label: 'Businesses', value: status.stats.businesses?.toLocaleString(), color: '#1A4C7C' },
              { label: 'Homeowners', value: status.stats.homeowners?.toLocaleString(), color: '#1D9E75' },
              { label: 'Landlords', value: ((status.stats.residential_landlords || 0) + (status.stats.commercial_landlords || 0)).toLocaleString(), color: '#E8A020' },
              { label: 'Professionals', value: status.stats.pi_professionals?.toLocaleString(), color: '#1A4C7C' },
              { label: 'Construction', value: status.stats.construction?.toLocaleString(), color: '#E8A020' },
              { label: 'Farmers', value: status.stats.farmers?.toLocaleString(), color: '#1D9E75' },
              { label: 'NFPs', value: status.stats.nfps?.toLocaleString(), color: '#5C6B7A' },
              { label: 'Strata', value: status.stats.strata?.toLocaleString(), color: '#5C6B7A' },
              { label: 'Sole Traders', value: status.stats.sole_traders?.toLocaleString(), color: '#00B4D8' },
              { label: 'With Claims', value: `${status.stats.with_claims?.toLocaleString()} (${status.stats.total ? Math.round(status.stats.with_claims/status.stats.total*100) : 0}%)`, color: '#E24B4A' },
              { label: 'Duration', value: status.completed_at && status.started_at ? `${Math.round((new Date(status.completed_at) - new Date(status.started_at)) / 1000)}s` : '...', color: '#5C6B7A' },
            ].map((s, i) => (
              <div key={s.label} className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
                <p className="text-[9px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>{s.label}</p>
                <p className="text-sm font-bold font-mono mt-0.5" style={{ color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Activity Log */}
      {status?.log?.length > 0 && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="mars-log">
          <div className="p-3 border-b border-[rgba(136,150,168,0.1)]">
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Agent Activity Log</h3>
          </div>
          <div className="max-h-60 overflow-y-auto p-3 space-y-1 font-mono text-[11px]">
            {[...status.log].reverse().map((entry, i) => (
              <div key={entry.timestamp + i} className="flex gap-2">
                <span style={{ color: '#5C6B7A' }}>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                <span className="font-bold" style={{ color: '#00B4D8' }}>[{entry.agent}]</span>
                <span style={{ color: '#0D1B2A' }}>{entry.message}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
